/**
 * ApprovalCenterPage — DIGEMP-APPROVAL-CENTER-EMPLOYEE-FRONTEND-081.
 *
 * Employee-centric decision center: every run awaiting a human decision
 * (awaiting_technician_review / awaiting_approval) is grouped under the Digital Employee that
 * produced it, together with the requested action, the exact target, the reasoning behind it and
 * the linked work (ticket / run / conversation / employee workspace).
 *
 * Decisions reuse the existing approval controls only: agentRunsApi.approve / agentRunsApi.reject
 * behind the same agents_approve gate and the same server rules as the run dashboard and run
 * detail page (self-approval refusal, empty customer draft refusal, Crisis Mode deferral).
 * Raw analysis JSON stays secondary, inside a collapsed box; the primary view is human-readable.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { agentRunsApi, agentsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { useNotification } from "../context/NotificationContext";
import { REJECT_QUICK_REASONS as _REJECT_QUICK_REASONS } from "../utils/agentRunConstants";
import { classifySource, SOURCE_STATES } from "../components/statusVocabulary";
import { classifySourceTruth } from "../components/sourceTruth";
import SourceTruthBadge from "../components/SourceTruthBadge";
import { useOnlineStatus } from "../components/useLiveState";
import { detailText } from "../utils/apiErrors";
import { parseUtc } from "../utils/utcDate";
import EmployeeAvatar from "../components/EmployeeAvatar";
import { degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";
import { actionTypeLabel } from "../components/actionTypeVocabulary";
import { taskTypeLabel } from "../components/taskTypeVocabulary";
import { FOCUS_RING_CLS, TOUCH_TARGET_CLS } from "../components/designSystem/designTokens";

const PENDING_LIMIT = 200;

const REVIEW_STATUSES = new Set(["awaiting_technician_review", "awaiting_approval"]);

const STATUS_LABELS = {
  awaiting_technician_review: "ממתין לסקירה",
  awaiting_approval:          "ממתין לאישור",
};

const STATUS_CHIP = {
  awaiting_technician_review: "bg-amber-100 text-amber-700 border-amber-200",
  awaiting_approval:          "bg-orange-100 text-orange-700 border-orange-200",
};

const RISK_LABELS = { green: "נמוך", amber: "בינוני", red: "גבוה" };
const RISK_BADGE = {
  green: "bg-green-50 text-green-700 border-green-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red:   "bg-red-50 text-red-700 border-red-200",
};

const LOW_CONFIDENCE = 60;

// ─── Small helpers ───────────────────────────────────────────────────────────

// close_ticket / send_customer_message always produce customer-facing output; escalate does too
// when it carries a customer_reply_draft (the same rule the dashboard's batch-approve exclusion
// uses). Used only to label the decision, never to weaken a control.
function isCustomerFacing(run) {
  const actionType = run.analysis?.proposed_action?.action_type;
  if (actionType === "close_ticket" || actionType === "send_customer_message") return true;
  if (actionType === "escalate" && run.analysis?.customer_reply_draft) return true;
  return false;
}

function conversationIdOf(run) {
  const id = run.context?.conversation_id;
  return typeof id === "string" && id ? id : null;
}

function initiatedByLabel(run) {
  const by = run.initiated_by;
  if (!by) return null;
  if (by === "auto:email_reply") return "אוטומטי — תגובת לקוח";
  if (by === "auto:new_ticket") return "אוטומטי — קריאה חדשה";
  if (by === "auto:scheduled_check") {
    return `אוטומטי — בדיקה מתוזמנת${run.context?.check_name ? `: ${run.context.check_name}` : ""}`;
  }
  return by;
}

const _ACTIVITY_RE = /^\[(customer|technician|note|ai_agent)\](?:\s*\([^)]*\))?\s*([\s\S]*)$/;

function lastCustomerMessage(recentActivity) {
  if (!recentActivity || typeof recentActivity !== "string") return null;
  const lines = recentActivity.split("\n").filter(Boolean);
  for (let i = lines.length - 1; i >= 0; i--) {
    const m = lines[i].match(_ACTIVITY_RE);
    if (m && m[1] === "customer") return m[2].trim();
  }
  return null;
}

function elapsedMinutes(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 60000);
}

function fmtWait(iso) {
  const mins = elapsedMinutes(iso);
  if (mins == null) return null;
  if (mins < 1) return "ממתין כעת";
  if (mins < 60) return `ממתין ${mins} דק׳`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return `ממתין ${h} שע׳${m ? ` ${m} דק׳` : ""}`;
  const d = Math.floor(h / 24);
  return `ממתין ${d} ${d === 1 ? "יום" : "ימים"}`;
}

function waitClass(iso) {
  const mins = elapsedMinutes(iso);
  if (mins == null) return "text-gray-400";
  if (mins >= 24 * 60) return "text-red-600 font-semibold";
  if (mins >= 4 * 60) return "text-amber-600 font-medium";
  return "text-gray-400";
}

// Absolute source timestamps for the audit trail. The API serialises naive UTC in places, so this
// uses the product's UTC-aware parser (utcDate.js) instead of `new Date(iso)`.
function sourceTimeLabel(iso) {
  if (!iso) return null;
  const parsed = parseUtc(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString("he-IL", { dateStyle: "short", timeStyle: "short" });
}

function confidenceColor(n) {
  if (n >= 70) return "bg-blue-500";
  if (n >= 50) return "bg-yellow-400";
  return "bg-red-400";
}

function employeeNameOf(run, employee) {
  return employee?.name || run.agent_name || "עובד לא מזוהה";
}

function ticketLabel(run) {
  const num = run.ticket_number != null ? `#${run.ticket_number}` : "קריאה ללא מספר";
  return run.ticket_title ? `${num} — ${run.ticket_title}` : num;
}

// ─── Decision outcome / approval-token lifecycle (existing API data only) ────
//
// A recorded decision is NOT an executed action. What actually happened is read from the run the
// approve request returns (status, result_summary and context.execution_result are all in
// AgentRunResponse) and from the lifecycle of the ApprovalToken the decision created (the
// approval-tokens endpoint every sibling surface consumes). Nothing is claimed that the data does
// not say — an outcome that cannot be read is stated as unknown.

function executionMessage(er) {
  return typeof er?.message === "string" && er.message.trim() ? er.message.trim() : null;
}

// Same crisis-signature the run detail page's execution-result block uses.
function isCrisisBlockedMessage(message) {
  return typeof message === "string" && (message.includes("מצב חירום") || message.includes("Crisis Mode"));
}

/**
 * The executor can report `success: true, executed: true` and still have failed to prove delivery:
 * `delivery_verified === false`, or a `mail_sent` field explicitly set to null, mean the side effect
 * was not verified. This mirrors `ticketEvidenceWorkflowModel.unprovenExecution` — the same rule the
 * technician rail already uses — so a manager's approval banner cannot disagree with the ticket view.
 */
function deliveryUnproven(er) {
  if (!er || typeof er !== "object") return false;
  if (er.delivery_verified === false) return true;
  return Object.prototype.hasOwnProperty.call(er, "mail_sent") && er.mail_sent == null;
}

function executionOutcome(run) {
  const unknown = { key: "unknown", text: "מצב הביצוע אינו ידוע — יש לפתוח את כרטיס הריצה." };
  if (!run) return unknown;

  const er = run.context?.execution_result;
  const hasEr = er != null && typeof er === "object" && Object.keys(er).length > 0;
  const msg = hasEr ? executionMessage(er) : null;

  // FAILED WORK IS NEVER COMPLETE. The run status is the backend's own verdict (manager_agent marks
  // the run failed when delivery is unverified or the mail outcome is unknown), so it outranks any
  // success-shaped execution_result the executor may also have stored. A failed run that claims
  // success is exactly the "unverified" case: report it as unproven, never as executed.
  if (run.status === "failed") {
    if (isCrisisBlockedMessage(msg)) return { key: "blocked", text: `הפעולה לא בוצעה — ${msg}` };
    if (hasEr && er.success === true) {
      return {
        key: "unverified",
        text: `הריצה סומנה כנכשלת — הביצוע אינו מאומת, ולכן הפעולה אינה מוצגת כהושלמה${
          msg ? ` (דיווח הביצוע: ${msg})` : ""
        }. יש לפתוח את כרטיס הריצה לפרטים.`,
      };
    }
    return {
      key: "failed",
      text: run.result_summary
        ? `הפעולה לא בוצעה — ${run.result_summary}`
        : msg
        ? `הפעולה לא בוצעה — ${msg}`
        : "הפעולה לא בוצעה — יש לפתוח את כרטיס הריצה לפרטים.",
    };
  }

  // `context.action_outcome` is the backend's postcondition verdict. Anything other than
  // `verified_success` is an admission that execution was not proven; `delivery_verified: false`
  // and an explicit null `mail_sent` say the same thing at the executor level.
  const outcome = run.context?.action_outcome;
  const outcomeStatus = outcome && typeof outcome === "object" ? outcome.status : null;
  const outcomeUnverified = typeof outcomeStatus === "string" && outcomeStatus !== "verified_success";
  const successShape = hasEr && er.success === true;
  if (successShape && (outcomeUnverified || deliveryUnproven(er))) {
    return {
      key: "unverified",
      text: `דווח ביצוע${msg ? ` (${msg})` : ""}, אך הוא לא אומת — אין להסיק שהפעולה הושלמה. יש לפתוח את כרטיס הריצה.`,
    };
  }
  if (!hasEr && outcomeUnverified && (run.status === "completed" || run.status === "resolved")) {
    return {
      key: "unverified",
      text: "הריצה הסתיימה ללא דיווח ביצוע מאומת — הפעולה אינה מוצגת כהושלמה. יש לפתוח את כרטיס הריצה.",
    };
  }

  if (hasEr) {
    if (er.success && er.executed) return { key: "executed", text: `הפעולה בוצעה${msg ? `: ${msg}` : "."}` };
    if (er.success) return { key: "completed_noop", text: `הריצה הושלמה ללא ביצוע פעולה${msg ? `: ${msg}` : "."}` };
    // The stored message itself names the crisis — repeating it in the prefix read as noise.
    if (isCrisisBlockedMessage(msg)) return { key: "blocked", text: `הפעולה לא בוצעה — ${msg}` };
    return { key: "failed", text: `הפעולה לא בוצעה${msg ? ` — ${msg}` : " — יש לפתוח את כרטיס הריצה לפרטים."}` };
  }

  // Manager_agent stores ctx.execution_result = {} when the run had no proposed action to run.
  if (!run.analysis?.proposed_action && (run.status === "completed" || run.status === "resolved")) {
    return { key: "completed_noop", text: "הריצה הושלמה — לא הוצעה פעולה לביצוע, ואישור ההחלטה לא ביצע דבר." };
  }
  if (run.status === "waiting_for_customer_connection") {
    return {
      key: "deferred",
      text: "הפעולה טרם בוצעה — אישור ההחלטה אינו ביצוע הפעולה; הביצוע יידרש לאחר השלמת החיבור המרוחק ללקוח.",
    };
  }
  if (REVIEW_STATUSES.has(run.status) || run.status === "approved") {
    return {
      key: "unconfirmed",
      text: "ההחלטה נרשמה, אך ביצוע הפעולה טרם נקבע. אישור ההחלטה אינו ביצוע הפעולה — מצב הביצוע המדויק מוצג בכרטיס הריצה.",
    };
  }
  if (run.status === "completed" || run.status === "resolved") {
    return {
      key: "unconfirmed",
      text: "הריצה הושלמה — לא נשמר דיווח ביצוע מפורש; פרטי הביצוע מוצגים בכרטיס הריצה.",
    };
  }
  return unknown;
}

const OUTCOME_STYLE = {
  executed:       "border-green-200 bg-green-50 text-green-800",
  // A no-op run executed nothing; green would read as "the requested work was done".
  completed_noop: "border-gray-300 bg-gray-50 text-gray-700",
  rejected:       "border-gray-300 bg-gray-50 text-gray-700",
  blocked:        "border-red-200 bg-red-50 text-red-700",
  failed:         "border-amber-200 bg-amber-50 text-amber-800",
  unverified:     "border-amber-200 bg-amber-50 text-amber-800",
  deferred:       "border-amber-200 bg-amber-50 text-amber-800",
  unconfirmed:    "border-gray-300 bg-gray-50 text-gray-700",
  unknown:        "border-gray-300 bg-gray-50 text-gray-600",
};

// ApprovalToken lifecycle — the precedence every surface in this product uses
// (revoked → used → expired → active) over the non-secret fields the endpoint exposes.
// expires_at is a naive-UTC string, so it is parsed as UTC, not local time.
export function approvalTokenState(token) {
  if (token?.revoked) return "revoked";
  if (token?.used) return "used";
  if (token?.expires_at && parseUtc(token.expires_at).getTime() <= Date.now()) return "expired";
  return "active";
}

function tokenStateText(token, state) {
  const label = actionTypeLabel(token.action_type) ?? "פעולה";
  const until = token.expires_at ? parseUtc(token.expires_at).toLocaleString("he-IL") : null;
  if (state === "revoked") {
    const why = token.revoke_reason === "crisis_mode"
      ? " בגלל מצב חירום"
      : token.revoke_reason ? ` (${token.revoke_reason})` : "";
    return `אסימון אישור (${label}) — בוטל${why}; הפעולה לא תבוצע.`;
  }
  if (state === "used") {
    return `אסימון אישור (${label}) — נוצל על ידי שכבת הביצוע; אין בכך ראיה שהפעולה הצליחה — תוצאת הביצוע מדווחת בנפרד.`;
  }
  if (state === "expired") {
    return `אסימון אישור (${label}) — פג תוקף מבלי שנוצל${until ? ` (${until})` : ""}; הפעולה לא תבוצע במסלול זה.`;
  }
  return `אסימון אישור (${label}) — בתוקף${until ? ` עד ${until}` : ""} וטרם נוצל; הביצוע טרם התרחש.`;
}

// The path an approval of this run will take — the same rule the backend's approval-context
// projection mirrors from manager_agent._handle_approve (connection gate first, then a run
// without a proposed action completes without executing), so a warning describes the path that
// will actually run. analysis.connection_required defaults to true exactly like the server.
function approvalPath(run) {
  const analysis = run.analysis;
  const connectionRequired = analysis?.connection_required ?? true;
  const diagnosticDone = analysis?.diagnostic_completed === true;
  if (connectionRequired && !diagnosticDone) return "connection";
  if (!analysis?.proposed_action) return "no_action";
  return "execute";
}

function crisisWarningText(run) {
  const path = approvalPath(run);
  if (path === "connection") {
    return "מצב חירום פעיל — אישור יבקש חיבור מרחוק, אך יצירת סשן החיבור תיחסם כל עוד מצב החירום פעיל, והפעולה לא תבוצע.";
  }
  if (path === "no_action") {
    return "מצב חירום פעיל — לא הוצעה פעולה לביצוע; אישור יסיים את הריצה ללא ביצוע פעולה.";
  }
  return "מצב חירום פעיל — הפעולה לא תבוצע: אישור יסתיים בכשל מדווח, ויידרש ניתוח חדש לאחר סיום מצב החירום.";
}

// The approval tokens the decision created, read live from the existing endpoint (the same
// ["run-approval-tokens", runId] key the ticket workbench uses). Loading, failure and an empty
// list are three different statements — an unknown state is never rendered as "no tokens".
function ApprovalTokenStates({ runId, enabled }) {
  const tokensQuery = useQuery({
    queryKey: ["run-approval-tokens", runId],
    queryFn: () => agentRunsApi.approvalTokens(runId),
    enabled: Boolean(enabled && runId),
    refetchInterval: 30000,
  });
  if (!enabled || !runId) return null;
  const { data: tokens, isLoading, isError } = tokensQuery;
  return (
    <div className="text-xs text-gray-600">
      <p className="font-medium text-gray-500">אסימוני האישור של הריצה:</p>
      {isLoading ? (
        <p className="text-gray-400">טוען את מצב אסימוני האישור…</p>
      ) : isError ? (
        <p className="text-amber-700">לא ניתן לטעון את מצב אסימוני האישור — המצב אינו ידוע; אין להסיק שלא נרשמו אסימונים.</p>
      ) : (tokens ?? []).length === 0 ? (
        <p className="text-gray-400">לא נרשמו אסימוני אישור לריצה זו.</p>
      ) : (
        <ul className="list-disc list-inside space-y-0.5">
          {tokens.map((token) => (
            <li key={token.id}>{tokenStateText(token, approvalTokenState(token))}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── DecisionItem ────────────────────────────────────────────────────────────

function DecisionItem({ run, employee, canApprove, crisisActive, returnTo, historicalReasons, expanded, onToggle, onDecided }) {
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState(null);
  // Opening the rejection form unmounts the "דחה" button. Without this, keyboard focus fell back
  // to <body> and the next Tab resumed at the top of the page. Focus the reason field on open and
  // return to the trigger on cancel (PROD-UX-076).
  const rejectTriggerRef = useRef(null);
  const rejectReasonRef = useRef(null);
  const wasRejectOpen = useRef(false);
  useEffect(() => {
    if (rejectOpen) {
      wasRejectOpen.current = true;
      rejectReasonRef.current?.focus();
    } else if (wasRejectOpen.current) {
      wasRejectOpen.current = false;
      rejectTriggerRef.current?.focus();
    }
  }, [rejectOpen]);

  const employeeName = employeeNameOf(run, employee);
  const conversationId = conversationIdOf(run);
  const panelId = `approval-decision-${run.id}`;
  const isCustomerAction = isCustomerFacing(run);
  const proposed = run.analysis?.proposed_action ?? null;

  const refreshPendingCounts = () => {
    qc.invalidateQueries({ queryKey: ["approvalCenter"] });
    // The three independent pending-count badges (nav, Dashboard, AgentsPage) each poll
    // agentsApi.list() under their own query key with no shared prefix.
    qc.invalidateQueries({ queryKey: ["agents-layout-badge"] });
    qc.invalidateQueries({ queryKey: ["agents-dash"] });
    qc.invalidateQueries({ queryKey: ["agents"] });
  };

  const approveMutation = useMutation({
    mutationFn: () => agentRunsApi.approve(run.id),
    // The approve response IS the post-decision truth (the route runs manager_agent before it
    // returns): read the outcome from it instead of presenting every approval as a success.
    onSuccess: (updatedRun) => {
      addNotification("הריצה אושרה ✓", "success");
      refreshPendingCounts();
      onDecided?.({
        decision: "approve",
        runId: run.id,
        ticketId: run.ticket_id,
        ticketNumber: run.ticket_number,
        conversationId,
        employeeName,
        outcome: executionOutcome(updatedRun),
      });
    },
    onError: (err) => {
      const text = detailText(err?.response?.data?.detail, "שגיאה באישור הריצה");
      setActionError(text);
      addNotification(text, "error");
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (rejectReason) => agentRunsApi.reject(run.id, rejectReason || undefined),
    onSuccess: () => {
      addNotification("הריצה נדחתה", "info");
      refreshPendingCounts();
      setRejectOpen(false);
      onDecided?.({
        decision: "reject",
        runId: run.id,
        ticketId: run.ticket_id,
        ticketNumber: run.ticket_number,
        conversationId,
        employeeName,
        outcome: { key: "rejected", text: "לא בוצעה פעולה — דחייה אינה מפעילה ביצוע." },
      });
    },
    onError: (err) => {
      const text = detailText(err?.response?.data?.detail, "שגיאה בדחיית הריצה");
      setActionError(text);
      addNotification(text, "error");
    },
  });

  const closeReject = useCallback(() => {
    setRejectOpen(false);
    setReason("");
  }, []);

  // Escape dismisses the reject form — same behaviour as the dashboard's RejectModal.
  useEffect(() => {
    if (!rejectOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === "Escape") closeReject(); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [rejectOpen, closeReject]);

  const busy = approveMutation.isPending || rejectMutation.isPending;

  const presetSet = new Set(_REJECT_QUICK_REASONS);
  const dynamicReasons = historicalReasons
    ? Object.entries(historicalReasons)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 6)
        .map(([r]) => r)
        .filter((r) => !presetSet.has(r))
    : [];
  const allReasons = [..._REJECT_QUICK_REASONS, ...dynamicReasons];

  const params = proposed?.parameters;
  const hasParams = params != null && typeof params === "object" && Object.keys(params).length > 0;
  const customerMessage = lastCustomerMessage(run.context?.recent_activity);
  const confidence = run.analysis?.confidence;

  // The backend's own projection of this decision — `GET /api/agent-runs/{id}/approval-context` — is
  // the only carrier of the effects list an approver is owed, and the only place the compensation
  // contract's answer reaches the decision: `get_run_approval_context` appends
  // `_NO_COMPENSATION_APPROVAL_NOTE` to `postconditions` on exactly the arm that will execute and
  // cannot be undone, so that it is read *before* the choice rather than after it. No surface in the
  // product called it, so every decision surface built its own account of what approving does and
  // none of them said the action cannot be undone. Fetched for the open row only: the queue holds up
  // to PENDING_LIMIT decisions and this is a per-run read.
  const effectsQuery = useQuery({
    queryKey: ["approvalContext", run.id],
    queryFn: () => agentRunsApi.approvalContext(run.id),
    enabled: expanded,
    staleTime: 30000,
  });
  const effects = effectsQuery.data ?? null;
  const effectsPostconditions = effects?.action?.postconditions ?? [];
  const effectsBlocking = effects?.action?.blocking_conditions ?? [];
  const effectsEmpty = effects != null && effectsPostconditions.length === 0 && effectsBlocking.length === 0;
  // The pilot default builds the deterministic mock when no ANALYSIS_PROVIDER is configured, and
  // marks it degraded: its prose is the adapter's own scaffolding, not a finding about this ticket.
  const degradedLabel = degradedAnalysisLabel(run.analysis?.degraded);
  const createdLabel = sourceTimeLabel(run.created_at);
  const updatedLabel = sourceTimeLabel(run.updated_at);

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={expanded ? panelId : undefined}
        className="w-full text-right px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium shrink-0 ${STATUS_CHIP[run.status] ?? "bg-gray-100 text-gray-600 border-gray-200"}`}>
            {STATUS_LABELS[run.status] ?? run.status}
          </span>
          <span className="text-sm font-medium text-gray-800 break-words min-w-0">{ticketLabel(run)}</span>
          {run.ticket_priority === "high" && <span className="text-xs text-red-600 font-semibold">⚠ עדיפות גבוהה</span>}
          {isCustomerAction && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">מול לקוח</span>}
          <span className={`mr-auto text-xs ${waitClass(run.created_at)}`}>{fmtWait(run.created_at)}</span>
          <span className="text-gray-400 text-xs" aria-hidden="true">{expanded ? "▲" : "▼"}</span>
        </span>
        <span className="block text-xs text-gray-600 mt-1 break-words">
          {proposed
            ? `⚡ ${actionTypeLabel(proposed.action_type) ?? "פעולה"}${proposed.target ? ` → ${proposed.target}` : ""}`
            : run.analysis
            ? "העובד לא הציע פעולה — פתחו לפרטים ולמידע החסר"
            : "טרם נשמר ניתוח לריצה זו — פתחו לפרטים"}
        </span>
      </button>

      {expanded && (
        <div id={panelId} role="region" aria-label={`החלטה — ${employeeName}, ${ticketLabel(run)}`} className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100">
          {/* ── What the employee is asking for ─────────────────────────────── */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-3">
            <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">הפעולה המבוקשת</p>
            {!run.analysis ? (
              <p className="text-sm text-gray-600">
                לא נשמר ניתוח לריצה זו — לא ניתן להציג פעולה מוצעת, יעד או נימוק.{" "}
                <Link to={`/agent-runs/${run.id}`} state={{ from: returnTo }} className="text-blue-700 underline">
                  פתחו את כרטיס הריצה
                </Link>{" "}
                לפרטים המלאים.
              </p>
            ) : !proposed ? (
              <p className="text-sm text-gray-600">העובד לא הציע פעולה לריצה זו.</p>
            ) : (
              <>
                <p className="text-sm text-gray-800 break-words">
                  <span className="font-medium">{actionTypeLabel(proposed.action_type) ?? "פעולה"}</span>
                  {proposed.description ? ` — ${proposed.description}` : ""}
                </p>
                <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                  <div className="flex gap-1 min-w-0">
                    <dt className="font-medium text-gray-500">היעד המדויק:</dt>
                    <dd className={`${proposed.target ? "text-gray-800" : "text-gray-400"} break-words min-w-0`}>
                      {proposed.target || "לא צוין יעד מדויק בפעולה המוצעת"}
                    </dd>
                  </div>
                  <div className="flex gap-1">
                    <dt className="font-medium text-gray-500">סוג הפעולה:</dt>
                    <dd>{proposed.action_type ?? "לא צוין"}</dd>
                  </div>
                </dl>
                {hasParams && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-gray-400 hover:text-gray-600 select-none">פרמטרים גולמיים (JSON)</summary>
                    <pre dir="ltr" className="mt-1 bg-white border border-gray-200 rounded p-2 overflow-x-auto text-[11px] text-gray-600">
                      {JSON.stringify(params, null, 2)}
                    </pre>
                  </details>
                )}
              </>
            )}
          </div>

          {/* ── Why — the evidence behind the request ──────────────────────── */}
          <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">על מה מתבססת ההחלטה</p>
            {/* Prefixed so it is read before the words it qualifies — the confidence meter, the
                problem statement and the recommended step below all belong to this same analysis. */}
            {degradedLabel && (
              <p
                data-testid="approval-degraded"
                className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
              >
                {degradedLabel}
              </p>
            )}
            {!run.analysis ? (
              <p className="text-xs text-gray-400">אין ניתוח שמור להצגה.</p>
            ) : (
              <>
                <div>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                    <span>ביטחון AI</span>
                    <span className="font-medium">{confidence != null ? `${confidence}%` : "לא ידוע"}</span>
                  </div>
                  {confidence != null && (
                    <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                      <div className={`h-full rounded-full ${confidenceColor(confidence)}`} style={{ width: `${confidence}%` }} />
                    </div>
                  )}
                  {confidence != null && confidence < 40 && (
                    <p className="text-xs text-red-700 mt-1">ביטחון נמוך מאוד — הניתוח ספקולטיבי. מומלץ לאסוף מידע נוסף לפני נקיטת פעולה.</p>
                  )}
                  {confidence != null && confidence >= 40 && confidence < LOW_CONFIDENCE && (
                    <p className="text-xs text-amber-600 mt-1">ביטחון נמוך — נדרשות ראיות נוספות לפני פעולה.</p>
                  )}
                  {confidence == null && <p className="text-xs text-gray-400 mt-1">ציון הביטחון לא נשמר בניתוח.</p>}
                </div>

                {(run.analysis.risk_level || run.analysis.task_type) && (
                  <div className="flex flex-wrap gap-1.5">
                    {run.analysis.risk_level && (
                      <span className={`text-xs px-2 py-0.5 rounded border font-medium ${RISK_BADGE[run.analysis.risk_level] ?? "bg-gray-50 text-gray-600 border-gray-200"}`}>
                        סיכון: {RISK_LABELS[run.analysis.risk_level] ?? run.analysis.risk_level}
                      </span>
                    )}
                    {run.analysis.task_type && (
                      <span className="text-xs px-2 py-0.5 rounded border font-medium bg-blue-50 text-blue-700 border-blue-200">
                        {taskTypeLabel(run.analysis.task_type)}
                      </span>
                    )}
                  </div>
                )}

                {run.analysis.likely_issue && (
                  <p className="text-xs text-gray-700"><span className="font-medium text-gray-500">בעיה אפשרית: </span>{run.analysis.likely_issue}</p>
                )}
                {run.analysis.diagnostic_synopsis && (
                  <p className="text-xs text-gray-700"><span className="font-medium text-gray-500">תוצאת אבחון: </span>{run.analysis.diagnostic_synopsis}</p>
                )}
                {run.analysis.missing_information?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500">מידע חסר לפני הכרעה:</p>
                    <ul className="list-disc list-inside text-xs text-gray-600">
                      {run.analysis.missing_information.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </div>
                )}
                {run.analysis.recommended_next_step && (
                  <p className="text-xs text-gray-700"><span className="font-medium text-gray-500">הצעד הבא: </span>{run.analysis.recommended_next_step}</p>
                )}
                {run.analysis.alternative_hypotheses?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500">השערות נוספות:</p>
                    <ul className="list-disc list-inside text-xs text-gray-600">
                      {run.analysis.alternative_hypotheses.map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Customer-facing draft: readable, never hidden behind JSON ──── */}
          {run.analysis?.customer_reply_draft && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-1">✉ תגובה מוצעת ללקוח</p>
              <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed break-words">{run.analysis.customer_reply_draft}</p>
              <p className="text-xs text-amber-600 mt-1">טיוטה בלבד — תישלח ללקוח רק אם הפעולה תאושר ותופעל.</p>
            </div>
          )}

          {/* ── Linked work ────────────────────────────────────────────────── */}
          <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">עבודה מקושרת</p>
            <ul className="text-xs text-gray-700 space-y-1 break-words min-w-0">
              <li>
                <span className="text-gray-500">קריאה: </span>
                <Link to={`/tickets/${run.ticket_id}?from=${encodeURIComponent(returnTo)}`} className="text-brand-600 hover:underline">
                  {ticketLabel(run)}
                </Link>
                {run.ticket_company_name && <span className="text-gray-400"> · {run.ticket_company_name}</span>}
                {run.ticket_technician_name && <span className="text-gray-400"> · טכנאי: {run.ticket_technician_name}</span>}
              </li>
              <li>
                <span className="text-gray-500">ריצת AI: </span>
                <Link to={`/agent-runs/${run.id}`} state={{ from: returnTo }} className="text-brand-600 hover:underline">פתח את כרטיס הריצה</Link>
                <span className="text-gray-400"> · ביקורת מלאה</span>
              </li>
              <li>
                <span className="text-gray-500">אחראי העובד: </span>
                {run.agent_id ? (
                  <Link to={`/agents/${run.agent_id}`} className="text-brand-600 hover:underline">
                    {employeeName} — סביבת העבודה
                  </Link>
                ) : (
                  <span>{employeeName}</span>
                )}
              </li>
              <li>
                <span className="text-gray-500">מקור הזמן: </span>
                נוצרה {createdLabel || "במועד שלא דווח"}
                {" · "}עודכנה {updatedLabel || "במועד שלא דווח"}
              </li>
              <li>
                <span className="text-gray-500">שיחה מקושרת: </span>
                {conversationId ? (
                  <Link to={`/conversations/${conversationId}`} className="text-brand-600 hover:underline">חזרה לשיחה</Link>
                ) : (
                  <span className="text-gray-400">לא מקושרת שיחה לריצה זו</span>
                )}
              </li>
              {initiatedByLabel(run) && (
                <li><span className="text-gray-500">הופעל על ידי: </span>{initiatedByLabel(run)}</li>
              )}
              {customerMessage && (
                <li className="text-gray-600">
                  <span className="text-gray-500">הודעת הלקוח האחרונה: </span>
                  "{customerMessage.length > 140 ? `${customerMessage.slice(0, 140)}…` : customerMessage}"
                </li>
              )}
            </ul>
          </div>

          {/* ── What approving this does — the effects list, read before the decision ── */}
          <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-1.5">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">השפעת האישור</p>
            {effectsQuery.isPending ? (
              <p role="status" className="text-xs text-gray-400">טוען את תיאור ההשפעה…</p>
            ) : !effects ? (
              // An unreadable projection is a fact about the record, not an absence of one: the
              // approver must not read "we could not fetch the effects" as "there is nothing to
              // warn about".
              <p
                data-testid="approval-effects-unreadable"
                className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
              >
                לא ניתן לקרוא מהשרת מה תיעשה הפעולה — אין להסיק מכך שהפעולה ניתנת לביטול. פתחו את כרטיס הריצה או נסו שוב.
              </p>
            ) : (
              <>
                {!effects.action?.executes_on_approval && (
                  <p className="text-xs text-gray-700">הפעולה לא תבוצע באישור זה — הריצה תסתיים בדיווח על אי-ביצוע.</p>
                )}
                {effectsPostconditions.map((item, i) => (
                  <p key={`post-${i}`} data-testid="approval-effect-item" className="text-xs text-gray-700">
                    {item}
                  </p>
                ))}
                {effectsBlocking.map((item, i) => (
                  <p key={`block-${i}`} data-testid="approval-blocking-item" className="text-xs text-red-700">
                    {item}
                  </p>
                ))}
                {effectsEmpty && (
                  <p className="text-xs text-gray-400">השרת לא דיווח על השפעה צפויה לפעולה זו.</p>
                )}
                {effects.self_approval_blocked && (
                  <p className="text-xs text-amber-800">אישור עצמי חסום — הפעלתם את הריצה, ולכן אישורה יסורב. דחייה מותרת.</p>
                )}
              </>
            )}
          </div>

          {/* ── Decision ───────────────────────────────────────────────────── */}
          <div className="rounded-lg border border-gray-200 p-3">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">החלטה</p>
            <p role="note" className="text-[11px] text-gray-500 mb-2">
              ההחלטה נשלחת לשרת בלבד. הרשאת agents_approve, בדיקות המדיניות, איסור אישור עצמי ומצב
              חירום נאכפים בשרת — המסך אינו עוקף אותם ואינו מעניק הרשאה.
            </p>
            {actionError && <p role="alert" className="text-xs text-red-600 mb-2">{actionError}</p>}
            {!canApprove ? (
              <p className="text-xs text-gray-500">
                אין לך הרשאת אישור (agents_approve) — ניתן לצפות בהחלטות ולפתוח את הריצה, אך לא להכריע.
              </p>
            ) : (
              <>
                {crisisActive && (
                  <p className="text-xs text-red-600 mb-2">{crisisWarningText(run)}</p>
                )}
                {isCustomerAction && (
                  <p className="text-xs text-amber-700 mb-2">
                    שימו לב: פעולה זו מופנית ללקוח. אישור מתחיל את מסלול הביצוע המאובטח בלבד — אין באישור עצמו ביצוע של הפעולה.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  {!rejectOpen && (
                    <button
                      type="button"
                      onClick={() => { setActionError(null); approveMutation.mutate(); }}
                      disabled={busy}
                      aria-label={`אשר את הבקשה של ${employeeName} — ${ticketLabel(run)}`}
                      className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50`}
                    >
                      {approveMutation.isPending ? "מאשר…" : "אשר"}
                    </button>
                  )}
                  {rejectOpen ? (
                    <div className="w-full space-y-2">
                      <div className="flex flex-wrap gap-1.5">
                        {allReasons.map((r) => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setReason(r)}
                            aria-pressed={reason === r}
                            className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} text-xs px-2.5 py-1 rounded-full border transition-colors ${
                              reason === r
                                ? "bg-red-100 border-red-400 text-red-700 font-medium"
                                : presetSet.has(r)
                                ? "bg-gray-50 border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600"
                                : "bg-amber-50 border-amber-200 text-amber-700 hover:border-red-300 hover:text-red-600"
                            }`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                      <textarea
                        ref={rejectReasonRef}
                        rows={2}
                        aria-label="סיבת הדחייה (אופציונלי)"
                        placeholder="סיבת הדחייה (אופציונלי)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => { setActionError(null); rejectMutation.mutate(reason); }}
                          disabled={busy}
                          className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50`}
                        >
                          {rejectMutation.isPending ? "שולח…" : "דחה החלטה"}
                        </button>
                        <button type="button" onClick={closeReject} disabled={busy} className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} border px-4 py-1.5 rounded-lg text-sm hover:bg-gray-50`}>
                          ביטול
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      ref={rejectTriggerRef}
                      onClick={() => { setActionError(null); setRejectOpen(true); }}
                      disabled={busy}
                      aria-label={`דחה את הבקשה של ${employeeName} — ${ticketLabel(run)}`}
                      className={`${TOUCH_TARGET_CLS} ${FOCUS_RING_CLS} border border-red-200 text-red-700 hover:bg-red-50 px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50`}
                    >
                      דחה
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ApprovalCenterPage() {
  const { can: canAgent } = useAgentPermissions();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const isOnline = useOnlineStatus();

  const canView = canAgent("agents_view");
  const canApprove = canAgent("agents_approve");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [expandedRunId, setExpandedRunId] = useState(null);
  const [lastDecision, setLastDecision] = useState(null);

  const runsQuery = useQuery({
    queryKey: ["approvalCenter", "pending"],
    queryFn: () => agentRunsApi.listAll({ status: "pending_review", limit: PENDING_LIMIT }),
    refetchInterval: 20000,
    enabled: canView,
  });
  const { data: runs = [], isLoading, refetch } = runsQuery;
  const queueState = classifySource(runsQuery);
  // Evidence-based header truth for the pending queue. A successful read while online with the
  // 20s poller reads POLLING; held data offline/paused reads CACHED; a failed refresh over held
  // data reads STALE; no usable payload reads UNAVAILABLE (or DENIED on 401/403).
  const queueTruth = classifySourceTruth(runsQuery, { online: isOnline, pollingMs: 20_000 });
  // failed = nothing to show at all; stale = a refresh failed after an earlier successful load —
  // the held queue is still shown, but it must be labelled as not-current instead of presented as
  // the live pending list.
  const queueFailed = queueState.key === SOURCE_STATES.failed;
  const queueStale = queueState.key === SOURCE_STATES.stale;
  const lastLoaded = runsQuery.dataUpdatedAt > 0 ? new Date(runsQuery.dataUpdatedAt).toLocaleTimeString("he-IL") : null;

  const { data: agents = [] } = useQuery({
    queryKey: ["agents"],
    queryFn: () => agentsApi.list(),
    staleTime: 30000,
    enabled: canView,
  });

  const { data: feedbackStats, isError: feedbackStatsFailed } = useQuery({
    queryKey: ["feedbackStats"],
    queryFn: () => agentRunsApi.feedbackStats(),
    staleTime: 120000,
    enabled: canApprove,
  });

  const { data: crisisMode, isError: crisisFailed } = useQuery({
    queryKey: ["crisis-mode"],
    queryFn: agentsApi.getCrisisMode,
    refetchInterval: 30000,
    enabled: canApprove,
  });
  // A failed fetch must not read the same as "not active" — the operator is told the state is
  // unknown instead (same distinction the run dashboard makes).
  const crisisActive = !crisisFailed && (crisisMode?.active ?? false);

  const historicalReasons = feedbackStats?.rejection_reason_distribution || null;

  const agentById = useMemo(() => {
    const map = new Map();
    for (const a of agents || []) map.set(a.id, a);
    return map;
  }, [agents]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (runs || [])
      .filter((r) => REVIEW_STATUSES.has(r.status))
      .filter((r) => !statusFilter || r.status === statusFilter)
      .filter((r) => {
        if (!q) return true;
        const hay = [
          r.ticket_number != null ? String(r.ticket_number) : "",
          r.ticket_title,
          r.ticket_company_name,
          r.agent_name,
          r.analysis?.proposed_action?.description,
          r.analysis?.proposed_action?.target,
          actionTypeLabel(r.analysis?.proposed_action?.action_type),
          r.analysis?.likely_issue,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // the longest-waiting decision first
  }, [runs, search, statusFilter]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const run of filtered) {
      const key = run.agent_id || "__unknown__";
      if (!map.has(key)) map.set(key, { key, runs: [] });
      map.get(key).runs.push(run);
    }
    return [...map.values()].map((g) => ({
      ...g,
      employee: agentById.get(g.runs[0].agent_id) ?? null,
    }));
  }, [filtered, agentById]);

  const summary = useMemo(() => {
    const oldest = filtered.length ? filtered[0].created_at : null;
    const lowConfidence = filtered.filter((r) => r.analysis?.confidence != null && r.analysis.confidence < LOW_CONFIDENCE).length;
    const customerFacing = filtered.filter(isCustomerFacing).length;
    return { total: filtered.length, employees: groups.length, oldest, lowConfidence, customerFacing };
  }, [filtered, groups]);

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות במרכז האישורים</div>;
  }

  const isFiltered = Boolean(search.trim() || statusFilter);

  return (
    <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">מרכז אישורים</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            ההחלטות שממתינות לך, מקובצות לפי עובד דיגיטלי — עם הפעולה המבוקשת, היעד המדויק והעבודה המקושרת.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SourceTruthBadge truth={queueTruth} testId="approval-center-source-truth" />
          <button
            type="button"
            onClick={() => refetch()}
            className="border px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50 text-gray-600"
          >
            רענן
          </button>
        </div>
      </div>

      {crisisActive && (
        <div className="rounded-lg bg-red-50 border border-red-300 px-4 py-3 flex items-center gap-3 text-sm text-red-700">
          <span className="text-base">⚠️</span>
          <span className="font-medium">מצב חירום פעיל — ביצוע פעולות חסום</span>
          <span className="text-red-600 text-xs">
            אישורים חדשים נרשמים, אך הפעולה לא תבוצע בהם, אסימוני אישור חיים נשללו, ויידרש ניתוח חדש לאחר סיום מצב החירום
          </span>
        </div>
      )}
      {crisisFailed && canApprove && (
        <div role="alert" className="rounded-lg bg-gray-50 border border-gray-300 px-4 py-3 flex items-center gap-3 text-sm text-gray-600">
          <span className="text-base">⚠️</span>
          <span className="font-medium">לא ניתן לטעון את סטטוס מצב החירום — המצב לא ידוע</span>
        </div>
      )}

      {!isOnline && (
        <p role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-sm p-3">
          {runsQuery.data != null
            ? "אין חיבור לרשת — ההחלטות לא מתרעננות כרגע; מוצג המצב מהטעינה האחרונה שהצליחה."
            : "אין חיבור לרשת — לא ניתן לטעון את ההחלטות הממתינות כרגע."}
        </p>
      )}

      {/* Decision recorded — the return path back into the conversation / ticket / run */}
      {lastDecision && (
        <div role="status" className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 space-y-2">
          <p className="text-sm text-green-800">
            ההחלטה נרשמה — {lastDecision.decision === "reject" ? "נדחתה" : "אושרה"} הבקשה של {lastDecision.employeeName}
            {lastDecision.ticketNumber != null ? ` · קריאה #${lastDecision.ticketNumber}` : ""}
          </p>
          {/* What the decision actually led to — read from the approve response and the token
              lifecycle, never assumed from the fact that a decision exists. */}
          {lastDecision.outcome && (
            <p className={`text-xs rounded border px-2.5 py-1.5 ${OUTCOME_STYLE[lastDecision.outcome.key] ?? OUTCOME_STYLE.unknown}`}>
              {lastDecision.outcome.text}
            </p>
          )}
          {lastDecision.decision === "approve" && (
            <ApprovalTokenStates runId={lastDecision.runId} enabled={canApprove} />
          )}
          <div className="flex flex-wrap items-center gap-3 text-sm">
            {lastDecision.conversationId ? (
              <Link to={`/conversations/${lastDecision.conversationId}`} className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg text-xs font-medium">
                → חזרה לשיחה
              </Link>
            ) : (
              <span className="text-xs text-gray-500">לריצה זו לא מקושרת שיחה.</span>
            )}
            {lastDecision.ticketId && (
              <Link to={`/tickets/${lastDecision.ticketId}?from=${encodeURIComponent(returnTo)}`} className="text-xs text-brand-600 hover:underline">
                פתח קריאה
              </Link>
            )}
            <Link to={`/agent-runs/${lastDecision.runId}`} state={{ from: returnTo }} className="text-xs text-brand-600 hover:underline">
              צפה בריצה
            </Link>
            <button type="button" onClick={() => setLastDecision(null)} className="mr-auto text-xs text-gray-400 hover:text-gray-600">
              סגור
            </button>
          </div>
        </div>
      )}

      {queueFailed ? (
        <div role="alert" className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 flex items-center justify-between gap-3 text-sm text-red-700">
          <span>לא ניתן לטעון את ההחלטות הממתינות.</span>
          <button type="button" onClick={() => refetch()} className="border border-red-300 px-3 py-1 rounded-lg text-xs hover:bg-red-100">
            נסה שוב
          </button>
        </div>
      ) : isLoading ? (
        <div role="status" className="rounded-lg border border-gray-200 bg-gray-50 text-gray-500 text-sm p-4 flex items-center gap-3">
          <span aria-hidden="true" className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-400" />
          טוען החלטות ממתינות…
        </div>
      ) : (
        <>
          {queueStale && isOnline && (
            <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm p-3 flex flex-wrap items-center justify-between gap-3">
              <span>
                רענון ההחלטות נכשל — מוצג המצב מהטעינה האחרונה שהצליחה
                {lastLoaded ? ` (${lastLoaded})` : ""}; ייתכן שהמצב אינו מעודכן.
              </span>
              <button
                type="button"
                onClick={() => refetch()}
                className="border border-amber-300 rounded-lg px-3 py-1 text-xs hover:bg-amber-100"
              >
                נסה שוב
              </button>
            </div>
          )}
          {/* Summary */}
          {summary.total > 0 && (
            <div className="flex gap-3 flex-wrap" aria-label="סיכום ההחלטות הממתינות">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                <span className="text-amber-700 font-semibold text-lg">{summary.total}</span>
                <span className="text-amber-700 text-sm mr-2">החלטות להכרעה</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-2">
                <span className="text-gray-800 font-semibold text-lg">{summary.employees}</span>
                <span className="text-gray-600 text-sm mr-2">עובדים ממתינים</span>
              </div>
              {summary.oldest && (
                <div className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm">
                  <span className="text-gray-600">וותיקה: </span>
                  <span className={waitClass(summary.oldest)}>{fmtWait(summary.oldest)}</span>
                </div>
              )}
              {summary.lowConfidence > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                  <span className="text-red-700 font-semibold text-lg">{summary.lowConfidence}</span>
                  <span className="text-red-700 text-sm mr-2">ביטחון נמוך (מתחת ל-{LOW_CONFIDENCE}%)</span>
                </div>
              )}
              {summary.customerFacing > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                  <span className="text-blue-700 font-semibold text-lg">{summary.customerFacing}</span>
                  <span className="text-blue-700 text-sm mr-2">פעולות מול לקוח</span>
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="חיפוש לפי קריאה, עובד, פעולה או יעד…"
              aria-label="חיפוש החלטות"
              className="border rounded-lg px-3 py-1.5 text-sm w-full sm:w-72 min-w-0"
            />
            {[
              { value: "", label: "הכל" },
              { value: "awaiting_approval", label: "ממתין לאישור" },
              { value: "awaiting_technician_review", label: "ממתין לסקירה" },
            ].map((opt) => (
              <button
                key={opt.value || "all"}
                type="button"
                onClick={() => setStatusFilter(opt.value)}
                aria-pressed={statusFilter === opt.value}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  statusFilter === opt.value
                    ? "bg-gray-800 text-white border-gray-800"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {runs.length >= PENDING_LIMIT && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              מוצגות {PENDING_LIMIT} הריצות הממתינות האחרונות בלבד — ייתכן שקיימות החלטות ותיקות יותר. לתצוגה מלאה:{" "}
              <Link to="/agent-runs" className="underline">ריצות AI</Link>.
            </p>
          )}

          {/* Queue */}
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center">
              <p className="text-sm text-gray-500">
                {isFiltered
                  ? "אין החלטות ממתינות שמתאימות לסינון הנוכחי."
                  : "אין החלטות הממתינות לאישור — כל העובדים הדיגיטליים סיימו את העבודה שממתינה להכרעה."}
              </p>
              {!isFiltered && <p className="text-xs text-gray-400 mt-1">ריצות חדשות יופיעו כאן אוטומטית.</p>}
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((group) => {
                const name = employeeNameOf(group.runs[0], group.employee);
                const groupId = `approval-group-${group.key}`;
                const oldest = group.runs[0].created_at;
                return (
                  <section key={group.key} aria-labelledby={groupId} className="bg-white rounded-xl border border-gray-200 shadow-sm">
                    <header className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gray-50/60 rounded-t-xl">
                      <EmployeeAvatar
                        name={name}
                        avatarKey={group.employee?.avatar_key}
                        avatarColor={group.employee?.avatar_color}
                        size="md"
                      />
                      <div className="min-w-0">
                        <h2 id={groupId} className="text-sm font-semibold text-gray-800">{name}</h2>
                        <p className="text-xs text-gray-500">
                          {group.employee?.role_title ? `${group.employee.role_title} · ` : ""}
                          {group.runs.length} {group.runs.length === 1 ? "החלטה ממתינה" : "החלטות ממתינות"}
                          {group.employee && group.employee.is_enabled === false ? " · העובד מושבת" : ""}
                        </p>
                      </div>
                      <div className="mr-auto flex items-center gap-3 text-xs">
                        <span className={waitClass(oldest)}>{fmtWait(oldest)}</span>
                        {group.employee && (
                          <Link to={`/agents/${group.employee.id}`} className="text-brand-600 hover:underline">
                            סביבת העובד ←
                          </Link>
                        )}
                      </div>
                    </header>
                    <ul className="divide-y divide-gray-100">
                      {group.runs.map((run) => (
                        <li key={run.id}>
                          <DecisionItem
                            run={run}
                            employee={group.employee}
                            canApprove={canApprove}
                            crisisActive={crisisActive}
                            returnTo={returnTo}
                            historicalReasons={feedbackStatsFailed ? null : historicalReasons}
                            expanded={expandedRunId === run.id}
                            onToggle={() => setExpandedRunId((cur) => (cur === run.id ? null : run.id))}
                            onDecided={setLastDecision}
                          />
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
